import {isValid, subDays} from 'date-fns';
import Decimal from 'decimal.js';
import {
  Choice,
  CurrencyInput,
  FormControl,
  FormField,
  Label,
  NumberInput,
  openConfirmDialog,
  OptionType,
  Separator,
} from 'platform/components';
import {Grid, GridItem, Heading, HStack, Show, Text, VStack} from 'platform/foundation';

import {UseFormReturn, useWatch} from 'react-hook-form';

import {clamp, head, isNil, isNotNil} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {useGetServiceContractsQuery} from '@dms/api/customer';
import {GetCustomerPaymentApiResponse} from '@dms/api/finance';
import {
  AftersalesPaymentMethod,
  Payment,
  PaymentFieldsState,
  PaymentUiCapabilities,
  usePutCheckoutSetTypeOfSaleMutation,
} from '@dms/api/sales';
import i18n from '@dms/i18n';
import {
  DEFAULT_FOREIGN_CURRENCY,
  EMPTY_PLACEHOLDER,
  handleApiError,
  MAX_DUE_DAYS,
  MIN_DUE_DAYS,
  PaymentDatesField,
  PredefinedNotes,
  resolvePaymentDates,
  useBank,
  useCashRegister,
  useTenant,
} from '@dms/shared';

import {
  CurrencyCodeType,
  getSafely,
  Nullish,
  precisionCalculation,
  RequiredTestIdProps,
  suffixTestId,
} from 'shared/web';

import {resolveDueOnPaymentMethodChange} from '../../../utils/resolveDueOnPaymentMethodChange';
import {useFormValueChange} from '../hooks/useFormValueChange';
import {CheckoutPaymentFormType} from '../types/CheckoutPaymentFormType';
import {getIsFieldRequired} from '../utils/getIsFieldRequired';
import {isCorrectiveTaxDocument} from '../utils/isCorrectiveTaxDocument';
import {PaymentDirection} from './PaymentDirection';

const MAX_TAXABLE_SUPPLY_DAYS_BEFORE_ISSUE = 15;
const MIN_PERCENTAGE = 0;
const MAX_PERCENTAGE = 100;
const PERCENTAGE_SCALE = 100;

interface CheckoutPaymentFormBodyProps extends RequiredTestIdProps {
  checkoutId: string;
  control: FormControl<CheckoutPaymentFormType>;
  customerId: string;
  formApi: UseFormReturn<CheckoutPaymentFormType>;
  uiCapabilities: PaymentUiCapabilities;
  payment: Payment;
  customerPayment: GetCustomerPaymentApiResponse | undefined;
  totalAmount?: number;
  onSaveChanges: (data: CheckoutPaymentFormType) => Promise<unknown> | undefined;
  isEditingDisabled?: boolean;
  isForeignCurrencyAllowed?: boolean;
}

// eslint-disable-next-line complexity, max-lines-per-function -- Pre-existing violation
export function CheckoutPaymentFormBody(props: CheckoutPaymentFormBodyProps) {
  const {control, formApi} = props;
  const {fieldState} = props.uiCapabilities;

  const fieldEnabled = (key: keyof PaymentFieldsState) =>
    getSafely(fieldState, key)?.state === 'EDIT';
  const fieldVisible = (key: keyof PaymentFieldsState) => {
    const field = getSafely(fieldState, key);
    return isNotNil(field) && field?.state !== 'HIDDEN';
  };
  const fieldRequired = getIsFieldRequired(fieldState);

  const serviceContracts = useGetServiceContractsQuery({customerId: props.customerId});
  const {aftersalesBankAccountOptions, getAftersalesDefaultBankAccount} = useBank();
  const {cashRegisterOptions} = useCashRegister();
  const {tenantCurrency} = useTenant();

  const [putCheckoutSetTypeOfSale] = usePutCheckoutSetTypeOfSaleMutation();

  const issueDate = useWatch({control: formApi.control, name: 'issueDate'});
  const paymentMethod = useWatch({control: formApi.control, name: 'method'});
  const note = useWatch({control: formApi.control, name: 'note'});
  const exchangeRate = useWatch({control: formApi.control, name: 'exchangeRate'});
  const amount = useWatch({control: formApi.control, name: 'amount'});
  const dueDays = useWatch({control: formApi.control, name: 'due'});

  const validIssueDate = isNotNil(issueDate) && isValid(issueDate) ? issueDate : null;
  const minTaxableEventDate = validIssueDate
    ? subDays(validIssueDate, MAX_TAXABLE_SUPPLY_DAYS_BEFORE_ISSUE)
    : new Date();
  const maxTaxableEventDate = validIssueDate ? validIssueDate : new Date();

  const serviceContractOptions =
    serviceContracts.data?.map((serviceContract) => ({
      label: serviceContract.number,
      value: serviceContract.serviceContractId,
    })) ?? [];

  const rawPercentage =
    isNotNil(props.totalAmount) && props.totalAmount !== 0
      ? parseFloat(
          precisionCalculation
            .multiply(precisionCalculation.divide(amount ?? 0, props.totalAmount), PERCENTAGE_SCALE)
            .toFixed(2)
        )
      : 0;
  const percentage = clamp(MIN_PERCENTAGE, MAX_PERCENTAGE, rawPercentage);

  useFormValueChange(
    exchangeRate,
    props.payment.exchangeRate ? parseFloat(props.payment.exchangeRate ?? '0') : null,
    (value) => formApi.setValue('exchangeRate', value)
  );

  // Falling back to the current form value keeps the form untouched when the API omits the field.
  useFormValueChange(amount, props.payment.paymentAmount?.amount ?? amount, (value) =>
    formApi.setValue('amount', value)
  );

  useFormValueChange(paymentMethod, props.payment.method ?? paymentMethod, (value) =>
    formApi.setValue('method', value)
  );

  const handleSaveChanges = () => {
    props.onSaveChanges(formApi.getValues());
  };

  const handlePercentageChange = (value: number | null) => {
    const clampedPercentage = clamp(MIN_PERCENTAGE, MAX_PERCENTAGE, value ?? MIN_PERCENTAGE);

    const totalAmount = props.totalAmount ?? 0;
    const roundedAmount = parseFloat(
      precisionCalculation.multiply(clampedPercentage / PERCENTAGE_SCALE, totalAmount).toFixed(2)
    );
    const newAmount = totalAmount > 0 ? Math.min(roundedAmount, totalAmount) : roundedAmount;

    formApi.setValue('amount', newAmount);
    handleSaveChanges();
  };

  const afterPaymentChange = (newValue: AftersalesPaymentMethod) => {
    const issuedOn = formApi.getValues('issueDate');
    const validIssuedOn = isNotNil(issuedOn) && isValid(issuedOn) ? issuedOn : null;

    if (isNotNil(validIssuedOn)) {
      const {due, dueDate} = resolveDueOnPaymentMethodChange({
        method: newValue,
        issueDate: validIssuedOn,
        invoiceMaturity: props.customerPayment?.invoiceMaturity,
      });

      formApi.setValue('due', due, {shouldValidate: true});
      formApi.setValue('dueDate', dueDate, {shouldValidate: true});
    }

    if (newValue === 'BANK_TRANSFER') {
      formApi.setValue(
        'bankAccount',
        getAftersalesDefaultBankAccount(props.payment.foreignCurrencyPaymentAmount?.currency)
          ?.accountNumber ?? '',
        {shouldValidate: true}
      );

      return;
    }

    if (newValue === 'CASH' && isNilOrEmpty(formApi.getValues('cashRegisterId'))) {
      formApi.setValue('cashRegisterId', head(cashRegisterOptions)?.value ?? '', {
        shouldValidate: true,
      });
    }
  };

  const handleSetPaymentMethod = (value: AftersalesPaymentMethod) => {
    formApi.setValue('method', value);
    afterPaymentChange(value);

    handleSaveChanges();
  };

  const handlePaymentMethodChange = (newValue: AftersalesPaymentMethod | Nullish) => {
    if (isNil(newValue) || newValue === paymentMethod) {
      return;
    }

    if (newValue === 'INTERNAL') {
      openConfirmDialog({
        onConfirm: () =>
          putCheckoutSetTypeOfSale({
            checkoutId: props.checkoutId,
            body: {typeOfSale: 'INTERNAL'},
          })
            .unwrap()
            .then(() => handleSetPaymentMethod(newValue))
            .catch(handleApiError),
        text: i18n.t('entity.checkout.labels.freeOfChargeHelperText'),
      });
      return;
    }

    if (paymentMethod === 'INTERNAL') {
      openConfirmDialog({
        onConfirm: () =>
          putCheckoutSetTypeOfSale({
            checkoutId: props.checkoutId,
            body: {typeOfSale: 'DEFAULT'},
          })
            .unwrap()
            .then(() => handleSetPaymentMethod(newValue))
            .catch(handleApiError),
        text: i18n.t('entity.invoice.notifications.freeOfChargeChangeHelperText'),
      });
      return;
    }

    handleSetPaymentMethod(newValue);
  };

  const updateDependentDates = (changedField: PaymentDatesField) => {
    const values = formApi.getValues();
    const patch = resolvePaymentDates(
      changedField,
      {
        issueDate: values.issueDate,
        // A new issue date with no due moves the due date onto the issue date. A cleared due
        // field must not move the due date.
        dueDays: changedField === 'issueDate' ? (values.due ?? MIN_DUE_DAYS) : values.due,
        dueDate: values.dueDate,
        taxableSupplyDate: values.dateOfTaxableSupply,
      },
      {maxTaxableSupplyDaysBeforeIssue: MAX_TAXABLE_SUPPLY_DAYS_BEFORE_ISSUE}
    );

    if (isNotNil(patch.dueDays)) {
      formApi.setValue('due', patch.dueDays, {shouldValidate: true});
    }
    if (isNotNil(patch.dueDate)) {
      formApi.setValue('dueDate', patch.dueDate, {shouldValidate: true});
    }
    if (isNotNil(patch.taxableSupplyDate)) {
      formApi.setValue('dateOfTaxableSupply', patch.taxableSupplyDate, {shouldValidate: true});
    }
  };

  // The backend recalculates the due days when the invoice recipient changes. The billing
  // information sits next to the payment, so the form follows without being remounted.
  useFormValueChange(dueDays, props.payment.due ?? dueDays, (value) => {
    formApi.setValue('due', value);
    updateDependentDates('dueDays');
  });

  const allOrderPaymentOptions: Array<OptionType<AftersalesPaymentMethod>> = [
    {label: i18n.t('entity.invoice.paymentMethod.bankTransfer'), value: 'BANK_TRANSFER'},
    {label: i18n.t('entity.invoice.paymentMethod.card'), value: 'CARD'},
    {label: i18n.t('entity.invoice.paymentMethod.cash'), value: 'CASH'},
    {label: i18n.t('entity.invoice.paymentMethod.cashOnDelivery'), value: 'CASH_ON_DELIVERY'},
    {label: i18n.t('general.labels.internal'), value: 'INTERNAL'},
  ];
  const availablePaymentMethods = props.payment.availablePaymentMethods ?? [];
  const orderPaymentOptions = allOrderPaymentOptions.filter((option) =>
    availablePaymentMethods.includes(option.value)
  );

  const foreignCurrencyAmount =
    exchangeRate && amount ? parseFloat(new Decimal(amount).div(exchangeRate).toFixed(2)) : 0;

  const isCorrective = isCorrectiveTaxDocument(props.payment);

  return (
    <VStack spacing={4}>
      <Grid spacing={4} columns={4}>
        <Show when={fieldVisible('paymentAmount')}>
          <FormField
            control={control}
            name="amount"
            type="currency"
            currency={tenantCurrency}
            label={i18n.t('general.labels.amount')}
            isDisabled={!fieldEnabled('paymentAmount') || props.isEditingDisabled}
            isRequired={fieldRequired('paymentAmount')}
            data-testid={suffixTestId('amount', props)}
          />
        </Show>
        <Show when={props.payment.discriminator === 'DEPOSIT' && fieldVisible('paymentAmount')}>
          <NumberInput
            suffix="%"
            value={percentage}
            onChange={handlePercentageChange}
            decimalPlaces={2}
            minStepperValue={MIN_PERCENTAGE}
            maxStepperValue={MAX_PERCENTAGE}
            isDisabled={!fieldEnabled('paymentAmount') || props.isEditingDisabled}
            label={i18n.t('entity.order.labels.percentage')}
            data-testid={suffixTestId('percentage', props)}
          />
        </Show>
        <Show when={isCorrective}>
          <PaymentDirection
            direction={props.payment.direction}
            data-testid={props['data-testid']}
          />
        </Show>
        <Show when={props.isForeignCurrencyAllowed}>
          <FormField
            control={control}
            name="exchangeRate"
            type="currency"
            currency={tenantCurrency}
            label={i18n.t('general.labels.exchangeRate')}
            isRequired
            isDisabled={props.isEditingDisabled}
            data-testid={suffixTestId('exchangeRate', props)}
          />
          <CurrencyInput
            value={foreignCurrencyAmount}
            isDisabled
            label={i18n.t('entity.checkout.labels.amountInForeignCurrency')}
            currency={
              (props.payment.foreignCurrencyPaymentAmount?.currency ??
                DEFAULT_FOREIGN_CURRENCY) as CurrencyCodeType
            }
          />
        </Show>
      </Grid>
      <Grid spacing={4} columns={4}>
        <Show when={fieldVisible('issueDate')}>
          <FormField
            control={control}
            name="issueDate"
            type="date"
            isRequired={fieldRequired('issueDate')}
            isRelativeDatesHidden
            label={i18n.t('entity.checkout.labels.issuedOn')}
            onChange={() => updateDependentDates('issueDate')}
            isDisabled={!fieldEnabled('issueDate') || props.isEditingDisabled}
            data-testid={suffixTestId('issueDate', props)}
            isNonClearable
          />
        </Show>
        <Show when={fieldVisible('due')}>
          <FormField
            control={control}
            name="due"
            type="number"
            label={i18n.t('entity.checkout.labels.due')}
            onChange={() => updateDependentDates('dueDays')}
            isDisabled={!fieldEnabled('due') || props.isEditingDisabled}
            isRequired={fieldRequired('due')}
            data-testid={suffixTestId('due', props)}
            minStepperValue={MIN_DUE_DAYS}
            maxStepperValue={MAX_DUE_DAYS}
            isStepperVisible
          />
        </Show>
        <Show when={fieldVisible('dueDate')}>
          <FormField
            control={control}
            name="dueDate"
            type="date"
            isRelativeDatesHidden
            label={i18n.t('entity.checkout.labels.dueDate')}
            onChange={() => updateDependentDates('dueDate')}
            isDisabled={!fieldEnabled('dueDate') || props.isEditingDisabled}
            isRequired={fieldRequired('dueDate')}
            data-testid={suffixTestId('dueDate', props)}
            isNonClearable
          />
        </Show>
        <Show when={fieldVisible('taxableEventDate')}>
          <FormField
            control={control}
            name="dateOfTaxableSupply"
            type="date"
            isRelativeDatesHidden
            label={i18n.t('entity.checkout.labels.taxableEventDate')}
            isDisabled={!fieldEnabled('taxableEventDate') || props.isEditingDisabled}
            isRequired={fieldRequired('taxableEventDate')}
            data-testid={suffixTestId('taxableEventDate', props)}
            maxDate={maxTaxableEventDate}
            minDate={minTaxableEventDate}
            isNonClearable
          />
        </Show>
        <Show when={fieldVisible('paymentMethod') && orderPaymentOptions.length > 0}>
          <GridItem span={1}>
            <Choice<AftersalesPaymentMethod>
              name="paymentMethod"
              value={paymentMethod}
              label={i18n.t('entity.checkout.labels.paymentMethod')}
              options={orderPaymentOptions}
              onChange={(value) => handlePaymentMethodChange(value)}
              isNotClearable
              isDisabled={!fieldEnabled('paymentMethod') || props.isEditingDisabled}
              data-testid={suffixTestId('paymentMethod', props)}
            />
          </GridItem>
        </Show>
        <Show when={fieldVisible('incomingBankAccount') && paymentMethod === 'BANK_TRANSFER'}>
          <GridItem span={1}>
            <FormField
              control={control}
              name="bankAccount"
              type="choice"
              options={aftersalesBankAccountOptions}
              label={i18n.t('entity.checkout.labels.incomingBankAccount')}
              isRequired={fieldRequired('incomingBankAccount')}
              isNotClearable
              isDisabled={!fieldEnabled('incomingBankAccount') || props.isEditingDisabled}
              data-testid={suffixTestId('incomingBankAccount', props)}
            />
          </GridItem>
        </Show>
        <Show when={fieldVisible('cashRegisterId') && paymentMethod === 'CASH'}>
          <GridItem span={1}>
            <FormField
              control={control}
              name="cashRegisterId"
              type="choice"
              options={cashRegisterOptions}
              label={i18n.t('entity.checkout.labels.cashRegister')}
              isRequired={fieldRequired('cashRegisterId')}
              isNotClearable
              isDisabled={!fieldEnabled('cashRegisterId') || props.isEditingDisabled}
              data-testid={suffixTestId('cashRegisterId', props)}
            />
          </GridItem>
        </Show>
        <Show when={fieldVisible('customerServiceContractId')}>
          <GridItem span={1}>
            <FormField
              control={control}
              name="customerServiceContractId"
              type="choice"
              options={serviceContractOptions}
              label={i18n.t('entity.checkout.labels.customerServiceContract')}
              isRequired={fieldRequired('customerServiceContractId')}
              isDisabled={!fieldEnabled('customerServiceContractId') || props.isEditingDisabled}
              data-testid={suffixTestId('customerServiceContract', props)}
            />
          </GridItem>
        </Show>
      </Grid>
      <Show when={fieldVisible('note')}>
        <VStack spacing={1}>
          <HStack justify="space-between" align="flex-end">
            <Label>{i18n.t('general.labels.note')}</Label>
            <PredefinedNotes
              note={note ?? null}
              onPrefill={(prefillNote) => props.formApi.setValue('note', prefillNote)}
              resource="SERVICE_CASE"
              context="service_checkout"
              isLinkVariant
              isEditingDisabled={!fieldEnabled('note') || props.isEditingDisabled}
              data-testid={suffixTestId('predefinedNotes', props)}
            />
          </HStack>
          <FormField
            control={control}
            name="note"
            type="textarea"
            isDisabled={!fieldEnabled('note') || props.isEditingDisabled}
            data-testid={suffixTestId('note', props)}
          />
        </VStack>
      </Show>
      <Show when={isCorrective}>
        <Separator spacing={0} />
        <VStack spacing={4}>
          <Heading size={4}>
            {i18n.t('entity.correctiveTaxDocument.labels.taxDocumentDetails')}
          </Heading>
          <Grid spacing={4} columns={4}>
            <GridItem span={1}>
              <VStack spacing={1}>
                <Label>{i18n.t('entity.invoice.labels.invoiceNumberLabel')}</Label>
                <Text size="small" data-testid={suffixTestId('correctedInvoiceNumber', props)}>
                  {props.payment.invoiceNumber ?? EMPTY_PLACEHOLDER}
                </Text>
              </VStack>
            </GridItem>
            <GridItem span={3}>
              <VStack spacing={1}>
                <Label>{i18n.t('general.labels.description')}</Label>
                <Text size="small" data-testid={suffixTestId('correctionDescription', props)}>
                  {props.payment.description ?? EMPTY_PLACEHOLDER}
                </Text>
              </VStack>
            </GridItem>
            <Show when={fieldVisible('dateOfReception')}>
              <GridItem span={1}>
                <FormField
                  control={control}
                  name="dateOfReception"
                  type="date"
                  isRelativeDatesHidden
                  label={i18n.t('entity.correctiveTaxDocument.labels.dateOfReception')}
                  isRequired={fieldRequired('dateOfReception')}
                  isDisabled={!fieldEnabled('dateOfReception') || props.isEditingDisabled}
                  data-testid={suffixTestId('dateOfReception', props)}
                />
              </GridItem>
            </Show>
            <Show when={fieldVisible('correctionReason')}>
              <GridItem span={3}>
                <FormField
                  control={control}
                  name="correctionReason"
                  type="text"
                  label={i18n.t('general.labels.reason')}
                  isRequired={fieldRequired('correctionReason')}
                  isDisabled={!fieldEnabled('correctionReason') || props.isEditingDisabled}
                  data-testid={suffixTestId('correctionReason', props)}
                />
              </GridItem>
            </Show>
          </Grid>
        </VStack>
      </Show>
    </VStack>
  );
}
